
import { useEffect, useState } from 'react'
import {
  Box,
  FormControlLabel,
  InputAdornment,
  Paper,
  Switch,

  TextField,
  Typography
} from '@mui/material'
import {
  AutoFixHigh as AutoIcon,
  Timeline as AxisIcon
} from '@mui/icons-material'
import type { YAxisConfig, YAxisPosition } from '@/interfaces/waveChart'

export interface AxisConfigEditorProps {
  /** Chart ID this axis belongs to */
  chartId: string
  
  /** Which axis this is configuring */
  axisId: YAxisPosition
  
  /** Current axis configuration */
  axisConfig?: YAxisConfig
  
  /** Callback when axis configuration changes */
  onAxisConfigChange?: (config: YAxisConfig) => void
  
  /** Whether the editor is disabled */
  disabled?: boolean
}

/**
 * Inline axis configuration editor for the Charts tab format panel
 * Modern, compact alternative to the existing AxisConfigModal
 */
export function AxisConfigEditor({
  axisId,
  axisConfig,
  onAxisConfigChange,
  disabled = false
}: AxisConfigEditorProps) {
  
  // Default configuration
  const defaultConfig: YAxisConfig = {
    label: '',
    unit: ''
    // range: undefined = auto-range mode (V3 schema)
  }
  
  const config = { ...defaultConfig, ...axisConfig }
  
  // Local state for input values to handle intermediate typing states
  const [minValue, setMinValue] = useState<string>((config.range?.min ?? 0).toString())
  const [maxValue, setMaxValue] = useState<string>((config.range?.max ?? 100).toString())
  
  // Sync local state when config changes externally
  useEffect(() => {
    setMinValue((config.range?.min ?? 0).toString())
  }, [config.range?.min])
  
  useEffect(() => {
    setMaxValue((config.range?.max ?? 100).toString())
  }, [config.range?.max])
  
  const handleConfigChange = (updates: Partial<YAxisConfig>) => {
    const newConfig = { ...config, ...updates }
    console.log('🔧 AXIS_CONFIG: AxisConfigEditor change', {
      axisId,
      autoRange: !newConfig.range, // true if no range set (auto-range)
      range: newConfig.range,
      updatedField: Object.keys(updates)[0]
    })
    onAxisConfigChange?.(newConfig)
  }
  
  const handleMinChange = (value: string) => {
    setMinValue(value)
    
    // Only update config for valid numbers
    const numericValue = parseFloat(value)
    if (!isNaN(numericValue)) {
      const currentRange = config.range || { min: 0, max: 100 }
      handleConfigChange({
        range: {
          min: numericValue,
          max: currentRange.max
        }
      })
    }
  }
  
  const handleMaxChange = (value: string) => {
    setMaxValue(value)
    
    // Only update config for valid numbers
    const numericValue = parseFloat(value)
    if (!isNaN(numericValue)) {
      const currentRange = config.range || { min: 0, max: 100 }
      handleConfigChange({
        range: {
          min: currentRange.min,
          max: numericValue
        }
      })
    }
  }
  
  const getAxisDisplayName = (axisId: YAxisPosition): string => {
    const names = {
      left: 'LEFT Y-AXIS',
      right: 'RIGHT Y-AXIS',
      left2: 'LEFT Y-AXIS 2',
      right2: 'RIGHT Y-AXIS 2'
    }
    return names[axisId]
  }

  const getAxisColor = (axisId: YAxisPosition): string => {
    // Fixed semantic colors for each axis position
    const colors = {
      left: '#1976d2',     // Blue
      right: '#d32f2f',    // Red  
      left2: '#388e3c',    // Green
      right2: '#f57c00'    // Orange
    }
    return colors[axisId]
  }

  return (
    <Paper 
      variant="outlined"
      sx={{ 
        p: 2,
        backgroundColor: 'background.paper'
      }}
    >
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        mb: 2 
      }}>
        <AxisIcon 
          fontSize="small" 
          sx={{ color: getAxisColor(axisId) }} 
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
          {getAxisDisplayName(axisId)}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Label and Unit */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            label="Label"
            size="small"
            fullWidth
            value={config.label || ''}
            onChange={(e) => handleConfigChange({ label: e.target.value })}
            placeholder="Axis title"
            disabled={disabled}
          />
          <TextField
            label="Unit"
            size="small"
            sx={{ minWidth: 80 }}
            value={config.unit || ''}
            onChange={(e) => handleConfigChange({ unit: e.target.value })}
            placeholder="°C"
            disabled={disabled}
          />
        </Box>

        {/* Auto Range Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={!config.range} // checked = auto-range (no range set)
              onChange={(e) => {
                if (e.target.checked) {
                  // Switch to auto-range: remove range
                  handleConfigChange({ range: undefined })
                } else {
                  // Switch to manual: set default range
                  handleConfigChange({ range: { min: 0, max: 100 } })
                }
              }}
              size="small"
              disabled={disabled}
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AutoIcon fontSize="small" />
              <Typography variant="body2">
                Auto Range
              </Typography>
            </Box>
          }
        />

        {/* Manual Range Controls */}
        {config.range && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Manual Range
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Min"
                size="small"
                type="number"
                value={minValue}
                onChange={(e) => handleMinChange(e.target.value)}
                disabled={disabled}
                InputProps={{
                  startAdornment: config.unit ? (
                    <InputAdornment position="start">
                      <Typography variant="caption" color="text.secondary">
                        {config.unit}
                      </Typography>
                    </InputAdornment>
                  ) : undefined
                }}
              />
              <TextField
                label="Max"
                size="small"
                type="number"
                value={maxValue}
                onChange={(e) => handleMaxChange(e.target.value)}
                disabled={disabled}
                InputProps={{
                  startAdornment: config.unit ? (
                    <InputAdornment position="start">
                      <Typography variant="caption" color="text.secondary">
                        {config.unit}
                      </Typography>
                    </InputAdornment>
                  ) : undefined
                }}
              />
            </Box>
          </Box>
        )}


      </Box>
    </Paper>
  )
} 